import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { SessionFilters } from "../../lib/filters";
import { aiApi } from "./api";
import { AiBlockedTooltip, AiUnavailableNote } from "./components/common";
import { useAiCapability } from "./hooks";
import "./ai.css";

/** Map interpreted filters onto the classic facet keys we support client-side. */
export function interpretedToFilters(f: Record<string, unknown>): SessionFilters {
  const out: SessionFilters = {};
  const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
  const b = str(f.browser);
  const o = str(f.os);
  const d = str(f.device);
  if (b) out.browser = b;
  if (o) out.os = o;
  if (d === "desktop" || d === "mobile" || d === "tablet") out.device = d;
  if (f.hasErrors === true || f.errors === true) out.hasErrors = true;
  if (f.hasGaps === true) out.hasGaps = true;
  const level = str(f.level);
  if (level) out.level = level as SessionFilters["level"];
  const route = str(f.route) ?? str(f.url) ?? str(f.urlContains);
  if (route) out.urlContains = route;
  const rel = str(f.release);
  if (rel) out.release = rel;
  const user = str(f.user) ?? str(f.endUserId);
  if (user) out.user = user;
  const from = str(f.from);
  const to = str(f.to);
  if (from) out.from = from.slice(0, 10);
  if (to) out.to = to.slice(0, 10);
  if (typeof f.sinceHours === "number" && f.sinceHours > 0 && !out.from) {
    out.from = new Date(Date.now() - f.sinceHours * 3_600_000).toISOString().slice(0, 10);
  }
  if (typeof f.minDurationSec === "number" && f.minDurationSec > 0) out.minDurationSec = f.minDurationSec;
  if (typeof f.maxDurationSec === "number" && f.maxDurationSec > 0) out.maxDurationSec = f.maxDurationSec;
  if (f.viewed === "viewed" || f.viewed === "unviewed") out.viewed = f.viewed;
  if (f.reviewed === "reviewed" || f.reviewed === "unreviewed") out.reviewed = f.reviewed;
  const q = str(f.q) ?? str(f.text);
  if (q) out.q = q;
  return out;
}

export function NlSearch({ query, listing, onApply, onClear }: { query: string; listing?: boolean; onApply: (filters: SessionFilters, q: string) => void; onClear: () => void }) {
  const ai = useAiCapability();
  const [pending, setPending] = useState(query);
  const [unmatched, setUnmatched] = useState(false);
  const [phase, setPhase] = useState<"idle" | "searching">("idle");
  const sawList = useRef(false);
  const search = useMutation({ mutationFn: (q: string) => aiApi.search(q) });
  useEffect(() => setPending(query), [query]);
  useEffect(() => {
    if (phase !== "searching") {
      sawList.current = false;
      return;
    }
    if (listing) {
      sawList.current = true;
      return;
    }
    if (sawList.current) {
      setPhase("idle");
      return;
    }
    const t = window.setTimeout(() => setPhase("idle"), 800);
    return () => clearTimeout(t);
  }, [phase, listing]);

  const run = () => {
    const q = pending.trim();
    if (!q) return;
    setUnmatched(false);
    setPhase("idle");
    search.mutate(q, {
      onSuccess: (page) => {
        const next = interpretedToFilters(page.interpreted.filters);
        if (Object.keys(next).length === 0) {
          setUnmatched(true);
          return;
        }
        setPhase("searching");
        onApply(next, q);
      },
    });
  };
  const noCredits = !ai.creditsAvailable;
  const off = ai.blocked || noCredits;
  const label = search.isPending ? "Thinking…" : phase === "searching" ? "Searching…" : "Ask";

  return (
    <div className="nl-search" data-testid="nl-search">
      <form
        className="nl-ask"
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
        autoComplete="off"
      >
        <span className="badge ai" aria-hidden="true">AI</span>
        <input className="input" value={pending} onChange={(e) => setPending(e.target.value)} placeholder="Search in your own words" aria-label="Ask about sessions" disabled={off} autoFocus autoComplete="off" />
        {(pending || query) && !search.isPending && (
          <button
            type="button"
            className="nl-clear"
            aria-label="Clear"
            title="Clear"
            onClick={() => {
              setPending("");
              setUnmatched(false);
              search.reset();
              if (query) onClear();
            }}
          >
            ×
          </button>
        )}
        <AiBlockedTooltip ai={ai}>
          <button type="submit" className="btn primary" disabled={search.isPending || phase === "searching" || !pending.trim() || off}>{label}</button>
        </AiBlockedTooltip>
      </form>
      {noCredits ? (
        <p className="muted small ai-unavailable" role="note" data-testid="ask-credits">Ask is paused until more AI credits are available.</p>
      ) : (
        <AiUnavailableNote ai={ai} />
      )}
      {search.isError && <div className="error-box" role="alert">{(search.error as Error).message}</div>}
      {unmatched && <div className="nl-unmatched muted small" role="status">Couldn’t turn that into a search. Try rephrasing.</div>}
    </div>
  );
}
